/**
 * Call Waiting Overlay — shows when a second call comes in while on a call.
 *
 * Displays caller info with Accept, Reject, and Hold & Accept buttons.
 * Includes a vibration pattern for mobile devices.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { queueService } from '../../interpreter-queue/InterpreterQueueService';
import { callWaitingRespond, callWaitingDismiss } from '../actions';
import { startCallAttention } from '../deafAccessibleAlert';
/** How long an unanswered waiting call stays on screen (the server ring window). */
const RING_WINDOW_MS = 30000;
/** How long a response may go unconfirmed before the card offers a retry. */
const RESPONSE_TIMEOUT_MS = 10000;
const CallWaitingOverlay = () => {
    const dispatch = useDispatch();
    const incomingCall = useSelector((state) => state['features/call-management']?.incomingCall);
    const [pending, setPending] = useState('');
    const [error, setError] = useState('');
    const pendingRef = useRef('');
    const responseTimer = useRef(undefined);
    const settle = () => {
        pendingRef.current = '';
        setPending('');
        if (responseTimer.current) {
            clearTimeout(responseTimer.current);
            responseTimer.current = undefined;
        }
    };
    // Deaf-accessible alert: vibrate where supported (mobile), and flash the
    // tab title on desktop where vibration is a no-op. The title flash clears
    // when the waiting call is answered, rejected, dismissed, or the caller
    // gives up — this card must never outlive the ring it announces.
    useEffect(() => {
        settle();
        setError('');
        if (!incomingCall) {
            return undefined;
        }
        if (typeof navigator.vibrate === 'function') {
            navigator.vibrate([200, 100, 200, 100, 200]);
        }
        const stop = startCallAttention({
            callerName: incomingCall.callerName || 'Malka contact',
            body: 'Call waiting on your current call.'
        });
        const matches = (data) => Boolean(data) && typeof data === 'object'
            && data.callId === incomingCall.callId;
        const gone = (data) => {
            if (matches(data))
                dispatch(callWaitingDismiss());
        };
        const responded = (data) => {
            if (matches(data)) {
                settle();
                dispatch(callWaitingDismiss());
            }
        };
        const refused = (data) => {
            if (!pendingRef.current || data.retrying)
                return;
            settle();
            setError(data.message || 'Your answer could not be confirmed. Please try again.');
        };
        queueService.on('p2pCallCancelled', gone);
        queueService.on('p2pCallEnded', gone);
        queueService.on('callWaitingResponded', responded);
        queueService.on('error', refused);
        const expiry = setTimeout(() => {
            if (!pendingRef.current)
                dispatch(callWaitingDismiss());
        }, RING_WINDOW_MS);
        return () => {
            stop();
            clearTimeout(expiry);
            settle();
            queueService.off('p2pCallCancelled', gone);
            queueService.off('p2pCallEnded', gone);
            queueService.off('callWaitingResponded', responded);
            queueService.off('error', refused);
        };
    }, [incomingCall?.callId, dispatch]);
    if (!incomingCall) {
        return null;
    }
    const respond = (action) => {
        if (pendingRef.current)
            return;
        setError('');
        pendingRef.current = action;
        setPending(action);
        try {
            dispatch(callWaitingRespond(incomingCall.callId, incomingCall.currentCallId, action));
        }
        catch {
            settle();
            setError('Your answer could not be sent. Check your connection and try again.');
            return;
        }
        // Reject is settled by the reducer (it clears the card); the accepts
        // wait for call_waiting_responded so a dropped answer is visible.
        responseTimer.current = setTimeout(() => {
            if (pendingRef.current !== action)
                return;
            settle();
            setError('Your answer could not be confirmed. Please try again.');
        }, RESPONSE_TIMEOUT_MS);
    };
    const handleAccept = () => respond('accept');
    const handleReject = () => respond('reject');
    const handleHoldAndAccept = () => respond('hold_and_accept');
    const busy = Boolean(pending);
    return (React.createElement("div", { className: 'call-waiting-overlay' },
        React.createElement("div", { className: 'call-waiting-card' },
            React.createElement("div", { className: 'call-waiting-icon' },
                React.createElement("i", { className: 'icon-phone-incoming' })),
            React.createElement("h3", null, "Incoming Call"),
            React.createElement("p", { className: 'call-waiting-caller' }, incomingCall.callerName),
            React.createElement("p", { "aria-live": 'polite', className: 'call-waiting-status' }, error || (busy ? 'Joining…' : 'Waiting on your current call')),
            React.createElement("div", { className: 'call-waiting-actions' },
                React.createElement("button", { className: 'call-waiting-btn reject', disabled: busy, onClick: handleReject },
                    React.createElement("i", { className: 'icon-phone-hangup' }),
                    React.createElement("span", null, "Reject")),
                React.createElement("button", { "aria-busy": pending === 'hold_and_accept', className: 'call-waiting-btn hold-accept', disabled: busy, onClick: handleHoldAndAccept },
                    React.createElement("i", { className: 'icon-phone-pause' }),
                    React.createElement("span", null, pending === 'hold_and_accept' ? 'Joining…' : 'Hold & Accept')),
                React.createElement("button", { "aria-busy": pending === 'accept', className: 'call-waiting-btn accept', disabled: busy, onClick: handleAccept },
                    React.createElement("i", { className: 'icon-phone' }),
                    React.createElement("span", null, pending === 'accept' ? 'Joining…' : 'Accept'))))));
};
export default CallWaitingOverlay;
